/**
 * Local viewpoint capture / replay layer.
 *
 * Builds on top of the BCF-shaped Viewpoint helpers in viewpoint.ts:
 *  • captureLocalViewpoint extends them with a JPEG thumbnail (rendered
 *    from the live viewer canvas) and a snapshot of the per-element
 *    visibility/transparency state that BCF doesn't carry.
 *  • applyLocalViewpoint restores camera + section + selection + the
 *    extra hidden/fade state.
 *  • thumbnail capture is best-effort: WebGL drawing buffers may have
 *    been swapped out before we read, so we force a synchronous render
 *    inside requestAnimationFrame and read in the same frame tick.
 */
import { useAppStore } from '@core/store'
import {
  applyViewpoint,
  captureViewpoint,
  semCena,
  type Viewpoint,
} from './viewpoint'
import { viewerApi } from './viewerApi'
import { largarRealceDoVer } from '@modules/bcf/lib/bcfVerImportado'
import * as THREE from 'three'
import {
  viewpointsStore,
  type LocalViewpoint,
  type ViewpointStateSnapshot,
} from './viewpointsStore'

const THUMB_W = 320
const THUMB_H = 240

async function captureAnchor(): Promise<[number, number, number] | null> {
  const store = useAppStore.getState()
  const byModel = new Map<string, number[]>()
  for (const target of store.selectedTargets) {
    const ids = byModel.get(target.modelId) ?? []
    ids.push(target.localId)
    byModel.set(target.modelId, ids)
  }
  const points: [number, number, number][] = []
  for (const [modelId, ids] of byModel) {
    const centers = await viewerApi.getWorldBoxCenters(modelId, ids)
    for (const center of centers) if (center) points.push(center)
  }
  if (points.length > 0) {
    const sum = points.reduce(
      (acc, point) => [
        acc[0] + point[0],
        acc[1] + point[1],
        acc[2] + point[2],
      ],
      [0, 0, 0] as [number, number, number],
    )
    return [
      sum[0] / points.length,
      sum[1] / points.length,
      sum[2] / points.length,
    ]
  }
  const controls = viewerApi.getWorld()?.camera.controls
  if (!controls) return null
  const target = controls.getTarget(new THREE.Vector3())
  return [target.x, target.y, target.z]
}

// Render the live scene synchronously and snapshot the back buffer
// into a downscaled JPEG Blob. Returns null if no world / no canvas /
// the capture failed (in any of those cases the caller will save the
// viewpoint without a thumbnail).
export async function captureThumbnail(
  width = THUMB_W,
  height = THUMB_H,
): Promise<Blob | null> {
  const world = viewerApi.getWorld()
  if (!world) return null
  return new Promise<Blob | null>((resolve) => {
    requestAnimationFrame(async () => {
      try {
        const renderer = world.renderer?.three
        const scene = world.scene?.three
        const cam = world.camera?.three
        if (!renderer || !scene || !cam) return resolve(null)
        // Synchronous render so the back buffer is fresh when we read.
        renderer.render(scene, cam)
        const src = renderer.domElement
        if (!src.width || !src.height) return resolve(null)

        // Aspect-fit the source canvas into the thumb rect (letterbox
        // with the panel background) so a wide window doesn't squash
        // the model into a flat strip.
        const srcAspect = src.width / src.height
        const dstAspect = width / height
        let dw = width
        let dh = height
        if (srcAspect > dstAspect) {
          dh = Math.round(width / srcAspect)
        } else {
          dw = Math.round(height * srcAspect)
        }
        const dx = Math.floor((width - dw) / 2)
        const dy = Math.floor((height - dh) / 2)

        const off =
          'OffscreenCanvas' in window
            ? new OffscreenCanvas(width, height)
            : (() => {
                const c = document.createElement('canvas')
                c.width = width
                c.height = height
                return c
              })()
        const ctx = (off.getContext('2d') as
          | OffscreenCanvasRenderingContext2D
          | CanvasRenderingContext2D
          | null)
        if (!ctx) return resolve(null)
        ctx.fillStyle = '#0b0b0c'
        ctx.fillRect(0, 0, width, height)
        ctx.drawImage(src, 0, 0, src.width, src.height, dx, dy, dw, dh)
        const blob = await canvasToBlob(off, 0.78)
        resolve(blob)
      } catch (err) {
        console.warn('[viewpointCapture] thumbnail failed', err)
        resolve(null)
      }
    })
  })
}

async function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  quality: number,
): Promise<Blob | null> {
  if ('convertToBlob' in canvas) {
    try {
      return await canvas.convertToBlob({ type: 'image/jpeg', quality })
    } catch {
      return null
    }
  }
  return new Promise((resolve) => {
    ;(canvas as HTMLCanvasElement).toBlob(
      (b) => resolve(b),
      'image/jpeg',
      quality,
    )
  })
}

async function snapshotState(): Promise<ViewpointStateSnapshot> {
  const store = useAppStore.getState()
  const idToHash = new Map(store.models.map((m) => [m.id, m.sha256]))

  const hiddenMap = await viewerApi.getHiddenItemsMap()
  const hidden: ViewpointStateSnapshot['hidden'] = []
  for (const [modelId, set] of hiddenMap) {
    const sha = idToHash.get(modelId)
    if (!sha) continue
    hidden.push({ sha256: sha, localIds: Array.from(set) })
  }

  const fadeMap = viewerApi.getTransparentItemsMap()
  const transparent: ViewpointStateSnapshot['transparent'] = []
  for (const [modelId, set] of fadeMap) {
    const sha = idToHash.get(modelId)
    if (!sha) continue
    transparent.push({ sha256: sha, localIds: Array.from(set) })
  }

  return {
    hidden,
    transparent,
    ghostMode: store.ghostMode,
    ghostOpacity: store.ghostOpacity,
    transparencyOpacity: store.transparencyOpacity,
  }
}

/**
 * Build, persist, and return a LocalViewpoint capturing the current
 * viewer state. Throws if no IFC is loaded (we don't capture empty
 * scenes).
 */
export async function captureLocalViewpoint(opts: {
  name: string
  notes?: string
  folder?: string | null
}): Promise<LocalViewpoint> {
  const store = useAppStore.getState()
  if (store.models.length === 0) {
    throw new Error('Carrega um IFC antes de capturar um viewpoint.')
  }

  const vp = await captureViewpoint()
  if (!vp) throw new Error('Falha a capturar a câmara atual.')

  const activeModel = store.models.find((m) => m.id === store.activeModelId)
  const primaryHash =
    activeModel?.sha256 ??
    store.models.find((m) => m.visible)?.sha256 ??
    store.models[0].sha256

  const [thumbnail, state, anchor] = await Promise.all([
    captureThumbnail(),
    snapshotState(),
    captureAnchor(),
  ])

  const folder = opts.folder ? opts.folder.trim() || null : null
  const now = new Date().toISOString()
  const record: LocalViewpoint = {
    id: cryptoRandomId(),
    name: opts.name.trim() || defaultName(),
    notes: opts.notes ?? '',
    createdAt: now,
    updatedAt: now,
    primaryHash,
    modelHashes: store.models.filter((m) => m.visible).map((m) => m.sha256),
    thumbnail,
    viewpoint: vp,
    state,
    anchor,
    folder,
  }
  await viewpointsStore.save(record)
  return record
}

/**
 * Re-capture camera + state + thumbnail and overwrite an existing
 * viewpoint. Preserves id, name, notes, folder, createdAt; only the
 * captured payload + updatedAt change. Throws if no IFC is loaded or
 * the record id no longer exists.
 */
export async function updateLocalViewpoint(
  id: string,
): Promise<LocalViewpoint> {
  const store = useAppStore.getState()
  if (store.models.length === 0) {
    throw new Error('Carrega um IFC antes de atualizar o viewpoint.')
  }
  const existing = await viewpointsStore.get(id)
  if (!existing) throw new Error('O viewpoint já não existe.')

  const vp = await captureViewpoint()
  if (!vp) throw new Error('Falha a capturar a câmara atual.')

  const activeModel = store.models.find((m) => m.id === store.activeModelId)
  const primaryHash =
    activeModel?.sha256 ??
    store.models.find((m) => m.visible)?.sha256 ??
    store.models[0].sha256

  const [thumbnail, state, anchor] = await Promise.all([
    captureThumbnail(),
    snapshotState(),
    captureAnchor(),
  ])

  const next: LocalViewpoint = {
    ...existing,
    primaryHash,
    modelHashes: store.models.filter((m) => m.visible).map((m) => m.sha256),
    thumbnail,
    viewpoint: vp,
    state,
    anchor,
    updatedAt: new Date().toISOString(),
  }
  await viewpointsStore.save(next)
  return next
}

function cryptoRandomId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  // Fallback (older browsers): timestamp + random.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function defaultName(): string {
  const d = new Date()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `Vista ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Restore everything captured in a LocalViewpoint to the live viewer.
 *
 * Order matters:
 *  1. Hidden state: apply BEFORE camera so the user sees the right
 *     items right away even if the camera animation is mid-flight.
 *  2. Camera + section + selection via applyViewpoint (BCF-spec).
 *  3. Transparency + ghost mode flags last so they overlay the final
 *     visibility set.
 *
 * Models that aren't currently loaded are skipped silently; caller
 * gets a `missing` list for the UI to surface.
 */
export async function applyLocalViewpoint(
  vp: LocalViewpoint,
): Promise<{ missing: string[] }> {
  // Imagem anexada: sem vista para repor. Não se mostram os escondidos
  // nem se mexe em transparências; applyViewpoint só avisa.
  if (semCena(vp.viewpoint)) {
    await applyViewpoint(vp.viewpoint)
    return { missing: [] }
  }
  // As cores e o fantasma de um "Ver" anterior de um importado saem antes
  // de repor o fantasma deste viewpoint (sair depois desfazia-o).
  await largarRealceDoVer()
  const store = useAppStore.getState()
  const hashToId = new Map(store.models.map((m) => [m.sha256, m.id]))
  const missing: string[] = []
  for (const sha of vp.modelHashes) {
    if (!hashToId.has(sha)) missing.push(sha)
  }

  // Restore hidden set: for each loaded model that matches, hide the
  // recorded localIds and show the rest.
  for (const m of store.models) {
    const entry = vp.state.hidden.find((h) => h.sha256 === m.sha256)
    const ids = entry ? new Set(entry.localIds) : new Set<number>()
    const model = viewerApi.getModel(m.id)
    if (!model) continue
    try {
      const visibleNow = await model.getItemsByVisibility(true)
      const hiddenNow = await model.getItemsByVisibility(false)
      const toHide = visibleNow.filter((id) => ids.has(id))
      const toShow = hiddenNow.filter((id) => !ids.has(id))
      if (toHide.length > 0) await model.setVisible(toHide, false)
      if (toShow.length > 0) await model.setVisible(toShow, true)
    } catch (err) {
      console.warn('[viewpointCapture] hide restore failed', err)
    }
  }
  // Bump the slice's hidden count + edges flag.
  await viewerApi._refreshVisibilityState()

  // Restore transparency.
  const fadeMap = new Map<string, Set<number>>()
  for (const entry of vp.state.transparent) {
    const modelId = hashToId.get(entry.sha256)
    if (!modelId) continue
    fadeMap.set(modelId, new Set(entry.localIds))
  }
  store.setTransparencyOpacity(vp.state.transparencyOpacity)
  await viewerApi.setTransparentItems(fadeMap)

  // Restore ghost mode (opacity then on/off; opacity setter is no-op
  // when ghostMode is already on with the same value).
  store.setGhostOpacity(vp.state.ghostOpacity)
  await viewerApi.setGhostMode(vp.state.ghostMode)

  // Apply BCF-shaped state last (camera, section, selection).
  await applyViewpoint(vp.viewpoint)

  return { missing }
}

export type { Viewpoint }
